// Polish Phase 5 (JUICE-03 / §9.7 / §12.4) — token-fly coins.
//
// When a word is claimed for tokens, a handful of Sun-Yellow coins arc from
// the board up to the bottom-bar coin counter (~420ms, staggered) — the visual
// half of the moment whose audio (token-clink) is wired in MatchScreen. INT-03:
// the companion coin-spark board burst belongs at the Pixi EffectLayer (z=4),
// NOT this DOM overlay — it is not emitted from here. Fire-and-forget DOM
// overlay driven by a monotonically-increasing `trigger`; coins are created
// imperatively and removed on finish (Web Animations API), so there is no
// React state churn per coin.
//
// Reduced motion: no coins fly (the counter still updates from the authoritative
// PlayerView). Test/SSR safe: bails when the DOM, the anchor rects, or
// element.animate are unavailable.

import { useStore } from '@nanostores/react'
import { type JSX, useEffect, useRef } from 'react'
import { $reducedMotion } from '../../stores/motion'

export interface TokenFlyProps {
  /** Monotonic trigger — increment to launch a volley. */
  readonly trigger: number
  /** Tokens earned this claim (coin count is capped at 6 for taste). */
  readonly count: number
  /** CSS selector for the launch origin (board region). */
  readonly originSelector: string
  /** CSS selector for the destination (coin counter). */
  readonly targetSelector: string
}

const MAX_COINS = 6

export function TokenFly({
  trigger,
  count,
  originSelector,
  targetSelector,
}: TokenFlyProps): JSX.Element {
  const reducedMotion = useStore($reducedMotion)
  const layerRef = useRef<HTMLDivElement>(null)
  const seenTrigger = useRef<number>(trigger)

  useEffect(() => {
    // Skip the initial mount + any non-advancing render.
    if (trigger === seenTrigger.current) return
    seenTrigger.current = trigger
    if (reducedMotion || count <= 0) return
    const layer = layerRef.current
    if (!layer || typeof document === 'undefined') return
    const origin = document
      .querySelector(originSelector)
      ?.getBoundingClientRect()
    const target = document
      .querySelector(targetSelector)
      ?.getBoundingClientRect()
    if (!origin || !target || origin.width === 0 || target.width === 0) return

    const ox = origin.left + origin.width / 2
    const oy = origin.top + origin.height * 0.4
    const tx = target.left + target.width / 2
    const ty = target.top + target.height / 2
    const n = Math.min(MAX_COINS, Math.max(1, count))
    const apexY = Math.min(oy, ty) - 70

    for (let i = 0; i < n; i++) {
      const coin = document.createElement('div')
      coin.className = 'token-fly-coin'
      coin.setAttribute('aria-hidden', 'true')
      layer.appendChild(coin)
      if (typeof coin.animate !== 'function') {
        coin.remove()
        continue
      }
      const jitterX = (i - (n - 1) / 2) * 16
      const midX = (ox + tx) / 2 + jitterX
      const anim = coin.animate(
        [
          {
            transform: `translate(${ox - 9}px, ${oy - 9}px) scale(0.5)`,
            opacity: 0,
          },
          {
            transform: `translate(${midX - 9}px, ${apexY - 9}px) scale(1)`,
            opacity: 1,
            offset: 0.4,
          },
          {
            transform: `translate(${tx - 9}px, ${ty - 9}px) scale(0.45)`,
            opacity: 0.85,
          },
        ],
        {
          duration: 420,
          delay: i * 45,
          easing: 'cubic-bezier(0.45, 0, 0.55, 1)',
          fill: 'forwards',
        },
      )
      const cleanup = (): void => {
        coin.remove()
      }
      anim.onfinish = cleanup
      anim.oncancel = cleanup
    }
  }, [trigger, count, reducedMotion, originSelector, targetSelector])

  return (
    <div
      ref={layerRef}
      aria-hidden="true"
      data-screen-region="token-fly"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 80,
      }}
    />
  )
}
